import { forwardRef, useRef, useState, useCallback, type CSSProperties } from 'react';
import { FaPencilAlt } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import { HiX } from 'react-icons/hi';

import { Modal, ModalMethods, Text, Button, toasts, Loader } from '../../../../../ui-component';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../../utils/common';
import IconSelectorModal, { IconType } from '../../AddItem/IconSelectorModal';
import pieceIcon from '../../../../../assets/images/Stack.png';

type OutfitPiece = {
  id: number;
  name: string;
  outfit_link?: string;
  icon_reference_id?: string;
};

type PiecesModalProps = {
  outfitName: string;
  outfitPieces: OutfitPiece[];
  customOutfitId?: number;
  onEditPiece?: (piece: OutfitPiece) => void;
  onDeletePiece?: (pieceId: number) => void;
  onAddPiece?: () => void;
  onPieceImageUpdate?: () => void;
};

type SortablePieceItemProps = {
  piece: OutfitPiece;
  disabled: boolean;
  isUpdatingImage: boolean;
  isSelectedForImage: boolean;
  onImageClick: (piece: OutfitPiece, e: React.MouseEvent) => void;
  onEdit: (piece: OutfitPiece) => void;
  onDelete: (pieceId: number) => void;
};

const SortablePieceItem = ({
  piece,
  disabled,
  isUpdatingImage,
  isSelectedForImage,
  onImageClick,
  onEdit,
  onDelete,
}: SortablePieceItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: piece.id,
    disabled,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  };

  const baseClass =
    'flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors';
  const dragStateClass = isDragging ? 'opacity-50' : '';
  const cursorClass = disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`${baseClass} ${dragStateClass} ${cursorClass}`}
      aria-label="Drag to reorder"
    >
      <div
        className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 overflow-hidden cursor-pointer hover:bg-blue-200 transition-colors relative group"
        onClick={(e) => onImageClick(piece, e)}
        onMouseDown={(e) => e.stopPropagation()}
        title="Click to update image"
      >
        {isUpdatingImage && isSelectedForImage ? (
          <div className="w-full h-full flex items-center justify-center">
            <Loader />
          </div>
        ) : piece.outfit_link ? (
          <img
            src={piece.outfit_link}
            alt={piece.name}
            className="w-full h-full object-cover rounded-lg"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-200 rounded-lg">
            <span className="text-gray-400 text-xs">+</span>
          </div>
        )}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all flex items-center justify-center">
          <Text
            size="small"
            color="white"
            className="opacity-0 group-hover:opacity-100 transition-opacity font-inter"
          >
            Change
          </Text>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <Text className="font-inter text-sm truncate" color="black">
          {piece.name}
        </Text>
      </div>
      <button
        type="button"
        onClick={() => onEdit(piece)}
        onMouseDown={(e) => e.stopPropagation()}
        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
        aria-label="Edit piece"
        disabled={disabled}
      >
        <FaPencilAlt size={14} />
      </button>
      <button
        type="button"
        onClick={() => onDelete(piece.id)}
        onMouseDown={(e) => e.stopPropagation()}
        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
        aria-label="Delete piece"
        disabled={disabled}
      >
        <MdDelete size={18} />
      </button>
    </div>
  );
};

const PiecesModal = forwardRef<ModalMethods, PiecesModalProps>(
  (
    {
      outfitName,
      outfitPieces,
      customOutfitId,
      onEditPiece,
      onDeletePiece,
      onAddPiece,
      onPieceImageUpdate,
    },
    ref
  ) => {
    const iconSelectorModalRef = useRef<ModalMethods>(null);
    const [selectedPieceForImage, setSelectedPieceForImage] = useState<OutfitPiece | null>(null);
    const [isUpdatingImage, setIsUpdatingImage] = useState(false);
    const [localOrder, setLocalOrder] = useState<number[] | null>(null);
    const [isSavingSort, setIsSavingSort] = useState(false);

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8,
        },
      })
    );

    const displayPieces: OutfitPiece[] =
      localOrder && outfitPieces?.length > 0
        ? (localOrder
            .map((id) => outfitPieces.find((p) => p.id === id))
            .filter((p): p is OutfitPiece => Boolean(p)) as OutfitPiece[])
        : outfitPieces ?? [];

    const handleEdit = (piece: OutfitPiece) => {
      if (onEditPiece) {
        onEditPiece(piece);
      }
    };

    const handleDelete = (pieceId: number) => {
      if (onDeletePiece) {
        onDeletePiece(pieceId);
      }
    };

    const handleImageClick = (piece: OutfitPiece, e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedPieceForImage(piece);
      iconSelectorModalRef.current?.show();
    };

    const handleIconSelect = async (icon: IconType) => {
      if (!selectedPieceForImage) {
        return;
      }

      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-missing');

        return;
      }

      setIsUpdatingImage(true);

      try {
        const iconRefId = icon.reference_id || icon.icon_id || icon.id || '';

        const response = await api.putRequest(`outfit_piece/update/${selectedPieceForImage.id}`, {
          name: selectedPieceForImage.name,
          boutique_id: Number(boutique_id),
          icon_reference_id: iconRefId,
          custom_outfit_id: customOutfitId,
        });

        if (response.status) {
          toasts('success', 'Piece image updated successfully', 'piece-image-update-success');

          if (onPieceImageUpdate) {
            onPieceImageUpdate();
          }
        } else {
          toasts(
            'error',
            response.message || 'Failed to update piece image',
            'piece-image-update-error'
          );
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'piece-image-update-error');
        }
      } finally {
        setIsUpdatingImage(false);
        setSelectedPieceForImage(null);
      }
    };

    const savePiecesOrder = useCallback(
      async (orderedIds: number[]) => {
        if (!customOutfitId || orderedIds.length === 0) {
          return;
        }

        setIsSavingSort(true);

        try {
          const response = await api.putRequest(
            `custom_outfit/swap/atomic/sort?customOutfitId=${customOutfitId}`,
            orderedIds
          );

          if (response.status) {
            toasts('success', 'Order updated', 'pieces-order-saved');
            setLocalOrder(null);

            if (onPieceImageUpdate) {
              onPieceImageUpdate();
            }
          } else {
            toasts('error', response.message || 'Failed to update order', 'pieces-order-failed');
            setLocalOrder(null);

            if (onPieceImageUpdate) {
              onPieceImageUpdate();
            }
          }
        } catch (err) {
          if (err instanceof Error) {
            toasts('error', err.message, 'pieces-order-failed');
          }
          setLocalOrder(null);

          if (onPieceImageUpdate) {
            onPieceImageUpdate();
          }
        } finally {
          setIsSavingSort(false);
        }
      },
      [customOutfitId, onPieceImageUpdate]
    );

    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
          return;
        }

        const currentPieces = displayPieces;
        const oldIndex = currentPieces.findIndex((p) => p.id === active.id);
        const newIndex = currentPieces.findIndex((p) => p.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
          return;
        }

        const newPieces = arrayMove(currentPieces, oldIndex, newIndex);
        const orderedIds = newPieces.map((p) => p.id);

        setLocalOrder(orderedIds);
        void savePiecesOrder(orderedIds);
      },
      [displayPieces, savePiecesOrder]
    );

    const headerComponent = (
      <div className="flex items-center justify-between w-full pr-8">
        <Text size="large" fontWeight={700} className="font-inter">
          Pieces - {outfitName}
        </Text>
        <button
          type="button"
          onClick={() => {
            if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
              ref.current.hide();
            }
          }}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <Text size="small" color="black" className="font-inter">
            Close
          </Text>
          <HiX size={20} />
        </button>
      </div>
    );

    return (
      <Modal
        ref={ref}
        size="xl"
        headerComponent={headerComponent}
        showFooter={false}
        showCloseIcon={false}
      >
        <div className="flex flex-col h-full min-h-[400px]">
          <div className="mb-4">
            <Text className="font-inter text-sm text-gray-600 mb-4 block mt-1" color="black">
              Manage all the pieces in this item. Add new pieces, choose icons, rename them, or
              delete what you don&apos;t need.
            </Text>

            <div className="flex items-center gap-2 mb-1">
              <img src={pieceIcon} alt="Pieces" className="w-4 h-4 object-contain" />
              <Text className="font-inter text-sm text-gray-700" color="black">
                Pieces in this item - {outfitPieces?.length || 0}
              </Text>
            </div>

            {outfitPieces && outfitPieces.length > 0 && (
              <Text className="font-inter text-xs text-gray-500 mb-2 block" color="black">
                {isSavingSort
                  ? 'Reordering disabled while saving'
                  : 'Drag any piece row to re-arrange'}
              </Text>
            )}
          </div>

          <div className="flex-1 overflow-y-auto pr-2  [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:hover:bg-gray-500">
            {displayPieces && displayPieces.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={displayPieces.map((piece) => piece.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div
                    className={`space-y-2 ${isSavingSort ? 'pointer-events-none opacity-60' : ''}`}
                  >
                    {displayPieces.map((piece) => (
                      <SortablePieceItem
                        key={piece.id}
                        piece={piece}
                        disabled={isSavingSort}
                        isUpdatingImage={isUpdatingImage}
                        isSelectedForImage={selectedPieceForImage?.id === piece.id}
                        onImageClick={handleImageClick}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="flex items-center justify-center h-[200px] p-5">
                <Text color="gray" size="small" className="font-inter">
                  No pieces available. Add your first piece below.
                </Text>
              </div>
            )}
          </div>

          <div className="border-t border-gray-300 pt-1 pb-1">
            <Button
              label="+ Add Piece"
              size="medium"
              bgColor="primary"
              onClick={onAddPiece}
              className="w-full"
            />
          </div>
        </div>

        <IconSelectorModal
          ref={iconSelectorModalRef}
          onIconSelect={handleIconSelect}
          selectedIconId={selectedPieceForImage?.icon_reference_id}
        />
      </Modal>
    );
  }
);

PiecesModal.displayName = 'PiecesModal';

export default PiecesModal;
